import { ThemeProvider } from '@material-ui/core';
import Snackbar from '@material-ui/core/Snackbar';
import { Alert } from '@material-ui/lab';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import errorSlice from '../store/error/slice';
import history from '../store/history';
import { AppState } from '../store/state';
import { AppDispatch } from '../store/store';
import Redirector from './Redirector';
import { appRoutes, getRouteByName } from './routes';
import theme from '../../src/theme';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { UserState } from '../store/user/types';
import { selectErrorState, selectUser } from '../store/selectors';
import { availableLanguages } from '../i18n';
import { ErrorState } from '../store/error/types';
import MaintenancePage from '../pages/MaintenancePage';

interface AppProps {
  errorState: ErrorState;
  closeSnackBarAction: () => void;
  userState: UserState;
}

const App = ({ errorState, closeSnackBarAction, userState }: AppProps) => {
  const { i18n } = useTranslation();

  const user = userState.data;
  const { openSnackBar, message, severity } = errorState;

  useEffect(() => {
    const browserLanguage = navigator.language.substring(0, 2);

    /*
      I check Redux for the language of the profile. 
      If not found, I check the browser language. 
      Finally, if it's not among the available languages, I use English.
    */
    const userLanguage = user.person.language
      ? user.person.language
      : availableLanguages.includes(browserLanguage)
      ? browserLanguage
      : 'en';

    i18n.changeLanguage(userLanguage);
  }, [user.person.language]);

  return (
    <ThemeProvider theme={theme}>
      <Router history={history}>
        <Switch>
          {appRoutes.map((route) => {
            return (
              <Route exact key={route.name} path={route.path}>
                <Redirector routeToAccess={route}>{route.fragment}</Redirector>
              </Route>
            );
          })}
          <Route path="/maintenance">
            <MaintenancePage />
          </Route>
          <Route path="*">
            <Redirect to={getRouteByName('RouteNotFound').path} />
          </Route>
        </Switch>
      </Router>
      {openSnackBar && (
        <StyledSnackbar open={openSnackBar} autoHideDuration={30000} onClose={() => closeSnackBarAction()}>
          <Alert severity={severity} onClose={() => closeSnackBarAction()}>
            {message}
          </Alert>
        </StyledSnackbar>
      )}
    </ThemeProvider>
  );
};

const mapStateToProps = (state: AppState) => ({
  errorState: selectErrorState(state),
  userState: selectUser(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  closeSnackBarAction: () => dispatch(errorSlice.actions.reset()),
});

const StyledSnackbar = styled(Snackbar)`
  top: 4.6rem;
  bottom: inherit !important;
  left: 56.5% !important;
  border: 1px solid #eee9e9;
  border-radius: 5px;
`;

export default connect(mapStateToProps, mapDispatchToProps)(App);
